import React, { useState, useEffect, useRef } from "react";
import {
    Input,
    Button,
    Text,
    FormControl,
    FormErrorMessage,
    useToast,
    CircularProgress,
    Center,
    Box,
    Heading,
    Container,
    VStack,
    FormLabel,
    HStack,
    Icon,
} from "@chakra-ui/react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { FiMail, FiCheckCircle, FiRefreshCw, FiArrowLeft, FiSend } from "react-icons/fi";
import { supabase } from "./utils/supabaseClient";
import { useAuth } from "./providers/AuthProvider";

const emailFromSearchParams = (searchParams) => {
    const raw = searchParams.get("email");
    if (!raw) return "";
    try {
        return decodeURIComponent(raw);
    } catch {
        return raw;
    }
};

const ForgotPasswordPage = () => {
    const { auth } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const toast = useToast();
    const [email, setEmail] = useState(() => emailFromSearchParams(searchParams));
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const emailInputRef = useRef(null);

    useEffect(() => {
        if (auth) {
            navigate("/enrolled-courses");
        }
    }, [auth, navigate]);

    useEffect(() => {
        const next = emailFromSearchParams(searchParams);
        if (next) setEmail(next);
    }, [searchParams]);

    useEffect(() => {
        const t = setTimeout(() => emailInputRef.current?.focus(), 100);
        return () => clearTimeout(t);
    }, []);

    const validateEmail = () => {
        const reg = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w\w+)+$/;
        const trimmed = email.trim();
        if (!trimmed) {
            setError("Email is required");
            return false;
        }
        if (!reg.test(trimmed)) {
            setError("Must be a valid email address");
            return false;
        }
        setError("");
        return true;
    };

    const sendResetEmail = async (emailAddress) => {
        const { error: supaError } = await supabase.auth.resetPasswordForEmail(
            emailAddress.trim(),
            {
                redirectTo: `${window.location.origin}/reset-password`,
            }
        );
        if (supaError) throw supaError;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateEmail()) return;
        setLoading(true);
        try {
            await sendResetEmail(email);
            setSent(true);
        } catch (err) {
            toast({
                title: err.message || "Failed to send reset email. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setLoading(true);
        try {
            await sendResetEmail(email);
            toast({
                title: "Reset link resent. Check your inbox.",
                status: "success",
                duration: 4000,
                isClosable: true,
            });
        } catch (err) {
            toast({
                title: err.message || "Failed to resend. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            minH="100vh"
            w="100%"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            px={{ base: 4, md: 0 }}
            py={8}
            bg={{ base: "gray.50", md: "white" }}
        >
            <Container maxW="container.sm" w="100%" px={{ base: 0, md: 4 }}>
                <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                >
                    <Box
                        bg="white"
                        p={{ base: 6, md: 8 }}
                        borderRadius="lg"
                        boxShadow={{ base: "sm", md: "md" }}
                        textAlign="center"
                        w="100%"
                    >
                        <VStack spacing={5} align="center">
                            {!sent ? (
                                <>
                                    <Box
                                        w={14}
                                        h={14}
                                        borderRadius="full"
                                        bg="blue.50"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        <Icon as={FiMail} boxSize={7} color="blue.600" />
                                    </Box>
                                    <Heading size="sm">Forgot your password?</Heading>
                                    <Text fontSize="sm" color="gray.600" maxW="sm">
                                        Enter your email and we&apos;ll send you a link to reset your
                                        password.
                                    </Text>
                                    <Box as="form" w="100%" onSubmit={handleSubmit}>
                                        <FormControl isInvalid={!!error}>
                                            <FormLabel
                                                fontSize={{ base: "sm", md: "md" }}
                                                textAlign="left"
                                                mb={2}
                                            >
                                                Email
                                            </FormLabel>
                                            <Input
                                                ref={emailInputRef}
                                                type="email"
                                                value={email}
                                                onChange={(e) => {
                                                    setEmail(e.target.value);
                                                    if (error) setError("");
                                                }}
                                                placeholder="you@example.com"
                                                bg="white"
                                                size={{ base: "md", md: "lg" }}
                                                fontSize={{ base: "16px", md: "md" }}
                                                minH={{ base: "48px", md: "40px" }}
                                                autoComplete="email"
                                                autoCapitalize="none"
                                                autoCorrect="off"
                                                inputMode="email"
                                            />
                                            {error && (
                                                <FormErrorMessage fontSize="xs" mt={1}>
                                                    {error}
                                                </FormErrorMessage>
                                            )}
                                        </FormControl>
                                        {loading ? (
                                            <Center py={4}>
                                                <CircularProgress
                                                    isIndeterminate
                                                    size={{ base: "30px", md: "35px" }}
                                                />
                                            </Center>
                                        ) : (
                                            <Button
                                                type="submit"
                                                mt={6}
                                                width="full"
                                                height={{ base: "48px", md: "44px" }}
                                                color="white"
                                                bg="gray.800"
                                                _hover={{ bg: "gray.700" }}
                                                _active={{ bg: "gray.900" }}
                                                leftIcon={<FiSend />}
                                                fontSize={{ base: "md", md: "lg" }}
                                                fontWeight="semibold"
                                                isDisabled={!email.trim()}
                                            >
                                                Send reset link
                                            </Button>
                                        )}
                                    </Box>
                                </>
                            ) : (
                                <>
                                    <Box
                                        w={14}
                                        h={14}
                                        borderRadius="full"
                                        bg="green.50"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        <Icon as={FiCheckCircle} boxSize={7} color="green.600" />
                                    </Box>
                                    <Heading size="sm" color="green.700">
                                        Check your email
                                    </Heading>
                                    <Text fontSize="sm" color="gray.600">
                                        We sent a password reset link to{" "}
                                        <Text as="span" fontWeight="semibold" color="gray.800">
                                            {email}
                                        </Text>
                                        . The link expires in 1 hour.
                                    </Text>
                                    <Box
                                        w="100%"
                                        bg="orange.50"
                                        borderWidth="1px"
                                        borderColor="orange.200"
                                        borderRadius="md"
                                        p={4}
                                        textAlign="left"
                                    >
                                        <Text fontSize="sm" fontWeight="medium" color="orange.900">
                                            Didn&apos;t receive the email?
                                        </Text>
                                        <Box
                                            as="ul"
                                            fontSize="sm"
                                            color="orange.800"
                                            pl={5}
                                            mt={2}
                                            listStyleType="disc"
                                            textAlign="left"
                                        >
                                            <Box as="li">Check your spam or junk folder</Box>
                                            <Box as="li">Make sure the email address is correct</Box>
                                        </Box>
                                    </Box>
                                    <Button
                                        width="full"
                                        variant="outline"
                                        borderColor="gray.300"
                                        onClick={handleResend}
                                        isLoading={loading}
                                        leftIcon={<FiRefreshCw />}
                                    >
                                        Resend reset link
                                    </Button>
                                    <Button
                                        variant="link"
                                        size="sm"
                                        color="gray.500"
                                        fontWeight="normal"
                                        onClick={() => setSent(false)}
                                    >
                                        Use a different email address
                                    </Button>
                                </>
                            )}
                        </VStack>
                    </Box>
                </motion.div>
            </Container>

            <Box mt={6} textAlign="center">
                <Button
                    as={Link}
                    to="/login"
                    variant="ghost"
                    size="sm"
                    leftIcon={<FiArrowLeft />}
                    color="gray.600"
                    _hover={{ color: "gray.900", bg: "transparent" }}
                >
                    Back to login
                </Button>
            </Box>

            <HStack mt={8} spacing={1.5} fontSize="xs" color="gray.400">
                <Text>Powered by</Text>
                <Box
                    as="img"
                    src={`${process.env.PUBLIC_URL || ""}/Edthos.png`}
                    alt="Edthos"
                    h="16px"
                    w="auto"
                    objectFit="contain"
                    opacity={0.4}
                />
            </HStack>
        </Box>
    );
};

export default ForgotPasswordPage;
